import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../lib/utils';

export const colorVariants = cva('text-muted-foreground', {
  variants: {
    variant: {
      foreground: 'text-foreground',
      muted: 'text-muted-foreground',
      primary: 'text-primary',
      secondary: 'text-secondary',
      accent: 'text-accent',
      destructive: 'text-destructive',
      /**
       * Inherit the parent's color instead of setting one. Required whenever the
       * container owns the color across states — an icon button whose
       * `hover:text-*` / `disabled:` classes must reach the glyph. Without it the
       * icon's own color wins over the inherited one and those states are silently
       * dead. Prefer this over restating the container's color on the icon.
       */
      inherit: 'text-current',
    },
  },
  defaultVariants: {
    variant: 'muted',
  },
});

export type ColorVariantsProps = VariantProps<typeof colorVariants>;

export function iconClassName(
  props: ColorVariantsProps | undefined,
  className?: string,
) {
  return cn(colorVariants(props), className);
}

export const sizeVariants = cva('', {
  variants: {
    size: {
      xs: 'h-3 w-3',
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6',
    },
  },
});

export type IconBaseProps = {
  size?: number | string;
  width?: number | string;
  height?: number | string;
  className?: string;
  /**
   * Tailwind classes for the icon's *inner* glyph (the foreground shape inside
   * the outer container), applied independently of `className` (which styles the
   * outer/background shape). Lets a caller colour the inner shape on its own —
   * e.g. the patient timeline drives it from `PRIORITY_INNER_CLASS_NAME` so the
   * glyph reads as a solid badge while the container keeps a pale tint. Optional:
   * icons fall back to their baked-in glyph colour when it's omitted. Icons that
   * aren't themeable (e.g. the fixed scheduled glyph) accept but ignore it.
   */
  innerClassName?: string;
} & ColorVariantsProps;

export function computeSize(
  { size, width, height }: Pick<IconBaseProps, 'size' | 'width' | 'height'>,
  defaultSize = 24,
) {
  const w = width ?? size ?? defaultSize;
  const h = height ?? size ?? defaultSize;
  return { w, h };
}

export function createIcon(
  render: (
    p: {
      ref: React.Ref<SVGSVGElement>;
      className: string;
      innerClassName?: string;
      width: number | string;
      height: number | string;
    } & React.SVGProps<SVGSVGElement>,
  ) => React.ReactElement,
  options?: {
    defaultVariant?: ColorVariantsProps['variant'];
    displayName?: string;
    defaultSize?: number;
    classNameBase?: 'stroke-current' | 'fill-current' | string;
  },
) {
  const Icon = React.forwardRef<
    SVGSVGElement,
    React.SVGProps<SVGSVGElement> & IconBaseProps
  >(
    (
      { className, variant, size, width, height, innerClassName, ...rest },
      ref,
    ) => {
      const { w, h } = computeSize(
        { size, width, height },
        options?.defaultSize ?? 24,
      );
      const colorClass = colorVariants({
        variant: variant ?? options?.defaultVariant ?? 'muted',
      });
      const base = options?.classNameBase ?? 'stroke-current';
      // `innerClassName` is destructured out of `...rest` so it's forwarded to the
      // render fn (for the inner glyph) rather than spread onto the <svg> DOM node.
      // Only forward it when set, so icons that don't consume it never receive an
      // unknown `innerClassName` prop on their `...rest` (which would leak to the DOM).
      return render({
        ref,
        className: cn(base, colorClass, className),
        width: w,
        height: h,
        ...(innerClassName === undefined ? {} : { innerClassName }),
        ...rest,
      });
    },
  );
  Icon.displayName = options?.displayName ?? 'Icon';
  return Icon;
}
